import { LegalLayout } from './LegalLayout';
import styles from './LegalLayout.module.css';

// PLACEHOLDER COPY — review with legal before production launch.
// The facts below (controller, data categories, retention) match the current
// architecture; tone + phrasing are boilerplate and may need polishing.
export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="2026-04-22">
      <div className={styles.placeholder}>
        Draft — reviewed against current architecture (EU GDPR compliance).
        Requires legal sign-off before production launch.
      </div>

      <h2>1. Controller</h2>
      <p>
        The controller of personal data processed through Markcat is <strong>Danube Mechatronics Kft.</strong>,
        1042 Budapest, József Attila utca 82. I/1, Hungary. Contact:{' '}
        <a href="mailto:office@danube-mechatronics.com">office@danube-mechatronics.com</a>.
      </p>

      <h2>2. What we collect</h2>
      <ul>
        <li><strong>Account data:</strong> email address and a salted password hash (bcrypt).</li>
        <li><strong>Bookmarks:</strong> URLs you save, any titles, fetched page content, and our generated
          summaries, categories, tags, and translations.</li>
        <li><strong>Billing data:</strong> for paying customers, a Stripe customer ID and subscription ID.
          Stripe is the processor for card data — we never see or store card numbers.</li>
        <li><strong>Usage counters:</strong> number of classifications, credit balances, fetch counts —
          needed to enforce your plan's allowance.</li>
        <li><strong>Technical logs:</strong> server logs may retain IP addresses and request metadata
          for up to 30 days for security and abuse prevention.</li>
      </ul>

      <h2>3. Legal bases (GDPR Art. 6)</h2>
      <ul>
        <li><strong>Contract performance</strong> for account data and bookmark storage — we cannot run
          the service without them.</li>
        <li><strong>Legitimate interest</strong> for anti-abuse logs and billing records.</li>
        <li><strong>Consent</strong> for any optional marketing email, withdrawable at any time.</li>
        <li><strong>Legal obligation</strong> for tax records we must retain under Hungarian accounting law.</li>
      </ul>

      <h2>4. Processors (sub-processors)</h2>
      <ul>
        <li><strong>Stripe, Inc.</strong> — payment processing. See{' '}
          <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer noopener">stripe.com/privacy</a>.
        </li>
        <li><strong>Hetzner Online GmbH</strong> (Germany) — server hosting.</li>
        <li><strong>LLM providers</strong> — when classifying your bookmarks, page content and title are
          sent to the provider you configure (Anthropic, OpenAI, OpenRouter, or a local Ollama).
          Self-hosted Ollama sends nothing to third parties. For the hosted service, the default
          provider is listed on the signup page; you may change it in Settings.</li>
      </ul>

      <h2>5. Retention</h2>
      <p>
        Your bookmarks are retained until you delete them or close your account. Soft-deleted bookmarks
        are purged after 7 days. Upon account deletion (see Section 7) all account data, bookmarks,
        credit ledger rows, and usage counters are erased within 72 hours from primary storage;
        backups are overwritten within 30 days.
      </p>
      <p>
        Invoice records required by Hungarian tax law are retained for 8 years in a segregated
        accounting system.
      </p>

      <h2>6. International transfers</h2>
      <p>
        Our primary infrastructure is in the EU (Germany). Some sub-processors may process data
        outside the EU under Standard Contractual Clauses — specifically LLM providers
        (United States) when a cloud-hosted LLM is selected.
      </p>

      <h2>7. Your rights (GDPR Arts. 15–22)</h2>
      <ul>
        <li><strong>Access and portability</strong> — download everything we hold on you as NDJSON from
          Settings → Account → "Download export".</li>
        <li><strong>Erasure</strong> — delete your account permanently from Settings → Account →
          "Delete my account". This cancels any active subscription and purges all your data.</li>
        <li><strong>Rectification</strong> — you can edit your bookmarks, categories, and settings
          directly in the app.</li>
        <li><strong>Objection / restriction</strong> — email us at the address above.</li>
        <li><strong>Complaint</strong> — you may lodge a complaint with the Hungarian Data Protection
          Authority (NAIH), <a href="https://www.naih.hu" target="_blank" rel="noreferrer noopener">naih.hu</a>.</li>
      </ul>

      <h2>8. Cookies</h2>
      <p>
        We use only technically necessary cookies (authentication token storage). We do not run
        third-party analytics or advertising trackers.
      </p>

      <h2>9. Changes</h2>
      <p>
        Material changes to this policy will be announced via email to registered users at least
        14 days before they take effect.
      </p>
    </LegalLayout>
  );
}
